import facepaint from 'facepaint';
import type { BalanceThemeRaw } from '@balance-web/theme';
import { useRawTheme } from '@balance-web/theme';

type BreakPoints = BalanceThemeRaw['breakpoints'];
type BreakPoint = keyof BreakPoints;

function formatUnits(value: string | number) {
  if (typeof value === 'number') {
    return `${value}px`;
  }

  return value;
}

/*
  Facepaint lets you write properties as arrays e.g.

  <div css={{ width: [200, 400] }} />

  By default, the first value of the array is 
    the default non-media query value unless you 
    override options. 
  More here: https://github.com/emotion-js/facepaint
*/
export const makeMq = (
  breakpoints: Array<{
    min?: number;
    max?: number;
  }>,
  options?: {
    literal?: boolean;
    overlap?: boolean;
  }
) => {
  return facepaint(
    breakpoints.map((w) => {
      return `@media ${
        w.min === undefined ? '' : `(min-width: ${formatUnits(w.min)})`
      } ${w.min !== undefined && w.max !== undefined ? ' and ' : ' '} ${
        w.max === undefined ? '' : `(max-width: ${formatUnits(w.max)})`
      }`;
    }),
    options
  );
};

const makeBalanceMq = (breakpoints: BreakPoints) => {
  return makeMq(
    Object.values(breakpoints).map((breakpoint) => {
      return {
        min: breakpoint,
      };
    })
  );
};

/*
  A helper for handling string OR array values e.g.

  <Component size="small" />
  VS
  <Component size={['small', 'large']} />
*/
const mapResponsiveProp = <
  Map extends Record<string, string | number>,
  Keys extends keyof Map
>(
  value: Keys | readonly (Keys | null)[],
  valueMap: Map
) => {
  if (Array.isArray(value)) {
    return value.map((k) => {
      return k == null ? null : valueMap[k];
    });
  }
  // @ts-ignore
  return valueMap[value];
};

// helper if array property declaration isn't appropriate
const makeMinBreak = (breakpoints: BreakPoints) => {
  return (key: BreakPoint, prefix = true) => {
    const width = breakpoints[key];
    const value = `(min-width: ${formatUnits(width)})`;

    return prefix ? `@media ${formatUnits(value)}` : value;
  };
};

// the breakpoints are designed to go up i.e. min-width
// if a max-width is necessary (hopefully rare) it's nice to provide a helper
const makeMaxBreak = (breakpoints: BreakPoints) => {
  return (key: BreakPoint, prefix = true) => {
    const width = breakpoints[key];
    const value = `(max-width: ${formatUnits(width - 1)})`;

    return prefix ? `@media ${formatUnits(value)}` : value;
  };
};

// FIXME:
// Should this even be a hook? I think we can just export these utilities...
export const useMediaQuery = () => {
  const rawTheme = useRawTheme();
  return {
    mq: makeBalanceMq(rawTheme.breakpoints),
    mapResponsiveProp,
    maxBreak: makeMaxBreak(rawTheme.breakpoints),
    minBreak: makeMinBreak(rawTheme.breakpoints),
  };
};
